/**
 * CompanyModuleView.jsx - Wrapper for å vise moduler inni CompanyLayout
 * 
 * VIKTIG: Tilgangssjekk bruker localStorage, ikke server.
 * 
 * Sjekker brukerens lokale modultilgang før rendering.
 * Redirecter til /company/dashboard hvis ingen tilgang.
 */

import React, { Suspense, lazy } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { Lock } from 'lucide-react';

// Lazy-load modulene
const ArbeidsrettPage = lazy(() => import('../../modules/arbeidsrett/pages/ArbeidsrettPage'));
const ArbeidsrettBedriftPage = lazy(() => import('../../modules/arbeidsrett-bedrift/pages/ArbeidsrettBedriftPage'));
const HusleierettPage = lazy(() => import('../../modules/husleierett/pages/HusleierettPage'));
const ForbrukerrettPage = lazy(() => import('../../modules/forbrukerrett/pages/ForbrukerrettPage'));
const BarnevernPageV2 = lazy(() => import('../../modules/barnevern_v2/pages/BarnevernPageV2'));

// Oppvekst & Utdanning - Master Grade moduler
const BarnehagePage = lazy(() => import('./BarnehagePage'));
const SkolePage = lazy(() => import('./SkolePage'));
const BarnevernMasterPage = lazy(() => import('./BarnevernMasterPage'));

// GDPR & Kontraktsbibliotek
const GdprPage = lazy(() => import('./GdprPage'));
const KontraktsbibliotekPage = lazy(() => import('./KontraktsbibliotekPage'));

// Husleierett Næring (B2B-eksklusiv)
const HusleierettNaeringPage = lazy(() => import('./HusleierettNaeringPage'));

// Loading spinner
const ModuleLoader = () => (
  <div className="flex items-center justify-center h-96">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-2 border-[#B87333]/30 border-t-[#B87333] rounded-full animate-spin" />
      <span className="text-[#8899A8] text-sm">Laster modul...</span>
    </div>
  </div>
);

// Access denied component
const AccessDenied = ({ moduleKey }) => (
  <div className="p-6">
    <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-12 text-center max-w-md mx-auto">
      <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
        <Lock className="w-8 h-8 text-red-400" />
      </div>
      <h2 className="text-lg font-semibold text-white mb-2">Ingen tilgang</h2>
      <p className="text-[#8899A8] mb-4">
        Du har ikke tilgang til denne modulen. 
        Kontakt administrator for å få tilgang.
      </p>
      <a 
        href="/company/dashboard"
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#B87333] hover:bg-[#A06328] text-white rounded-lg transition-colors"
      >
        Gå til dashboard
      </a>
    </div>
  </div>
);

// Mapping av moduleKey til komponent
const MODULE_COMPONENTS = {
  arbeidsrett: ArbeidsrettPage,
  'arbeidsrett-bedrift': ArbeidsrettBedriftPage,
  husleierett: HusleierettPage,
  forbrukerrett: ForbrukerrettPage,
  barnevern: BarnevernPageV2,
  // Oppvekst & Utdanning - Master Grade moduler
  barnehage: BarnehagePage,
  skole: SkolePage,
  'barnevern-master': BarnevernMasterPage,
  // GDPR & Kontraktsbibliotek
  gdpr: GdprPage,
  kontraktsbibliotek: KontraktsbibliotekPage,
  // Husleierett Næring (B2B-eksklusiv)
  'husleierett-naering': HusleierettNaeringPage,
};

// Gyldige modulnøkler
const VALID_MODULES = ['arbeidsrett', 'arbeidsrett-bedrift', 'husleierett', 'forbrukerrett', 'barnevern', 'barnehage', 'skole', 'barnevern-master', 'gdpr', 'kontraktsbibliotek', 'husleierett-naering'];

export default function CompanyModuleView() {
  const { moduleKey } = useParams();
  const { hasModuleAccess, loading } = useCompany();

  // Vis loader mens vi sjekker tilgang
  if (loading) {
    return <ModuleLoader />;
  }

  // Sjekk om moduleKey er gyldig
  if (!moduleKey || !VALID_MODULES.includes(moduleKey)) {
    return <Navigate to="/company/modules" replace />;
  }

  // Moduler med teaser-modus (vises selv uten tilgang)
  const TEASER_MODULES = ['arbeidsrett-bedrift', 'husleierett-naering', 'barnehage', 'skole', 'barnevern-master', 'gdpr', 'kontraktsbibliotek'];

  // Sjekk tilgang fra localStorage (via CompanyContext)
  const hasAccess = hasModuleAccess(moduleKey);
  
  if (!hasAccess && !TEASER_MODULES.includes(moduleKey)) {
    // Redirect til dashboard hvis ingen tilgang (og ikke teaser-modul)
    return <Navigate to="/company/dashboard" replace />;
  }

  const ModuleComponent = MODULE_COMPONENTS[moduleKey];

  if (!ModuleComponent) {
    return (
      <div className="p-6">
        <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-12 text-center">
          <h2 className="text-lg font-medium text-white mb-2">Modul ikke funnet</h2>
          <p className="text-[#8899A8]">
            Modulen "{moduleKey}" er ikke tilgjengelig.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid={`company-module-${moduleKey}`}>
      <Suspense fallback={<ModuleLoader />}>
        <ModuleComponent />
      </Suspense>
    </div>
  );
}
